/**
 * Keyboard helpers for the App-level shortcut handler (spec §17).
 *
 * Letters match by physical `code` ("KeyJ") OR by lower-cased `key`, so shortcuts work on an
 * Arabic layout where `key` is an Arabic letter but `code` is still the Latin position.
 */

/** Selector of elements whose keystrokes must never trigger shortcuts. */
export const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="textbox"]';

/** Chords (`g` then `i`) expire after this many milliseconds. */
export const CHORD_TIMEOUT_MS = 1200;

/** True when the event originated inside a text-entry element. */
export function isEditable(target: EventTarget | null | undefined, ev?: Event): boolean {
  const el = (ev?.composedPath?.()[0] ?? target) as Element | null | undefined;
  return !!el?.closest?.(EDITABLE_SELECTOR);
}

/** Letter match by `code` or `key` (case-insensitive). `letter` must be a single a–z. */
export function keyIs(ev: KeyboardEvent, letter: string): boolean {
  const l = letter.toLowerCase();
  return ev.code === 'Key' + l.toUpperCase() || ev.key.toLowerCase() === l;
}

/**
 * Global guard: modifiers, IME composition (`isComposing` / keyCode 229) and an open dialog
 * all suppress shortcuts.
 */
export function shouldIgnoreShortcut(ev: KeyboardEvent, dialogOpen = false): boolean {
  return ev.ctrlKey || ev.metaKey || ev.altKey || ev.isComposing || ev.keyCode === 229 || dialogOpen;
}

/** `?`, `؟` or Shift+/ — the keyboard-help toggle. */
export function isHelpKey(ev: KeyboardEvent): boolean {
  return ev.key === '?' || ev.key === '؟' || (ev.shiftKey && ev.code === 'Slash');
}

/** `*` or Shift+8 — the selection chord prefix. */
export function isStarKey(ev: KeyboardEvent): boolean {
  return ev.key === '*' || (ev.shiftKey && ev.code === 'Digit8');
}

/**
 * A two-key chord prefix (`g …`, `* …`). Call `arm()` on the prefix, then `consume()` on the
 * next key: it returns true only within the timeout and resets the state either way.
 */
export class Chord {
  /** Arm time, or null when not armed (a clock may legitimately start at 0). */
  private at: number | null = null;
  constructor(private readonly timeoutMs = CHORD_TIMEOUT_MS, private readonly now: () => number = Date.now) {}

  arm(): void {
    this.at = this.now();
  }

  /** Whether the chord is armed and still within the window (without consuming it). */
  active(): boolean {
    return this.at !== null && this.now() - this.at < this.timeoutMs;
  }

  /** Consume the chord: true if it was active. */
  consume(): boolean {
    const ok = this.active();
    this.at = null;
    return ok;
  }

  reset(): void {
    this.at = null;
  }
}

/** Ctrl/Meta+Enter inside compose (spec §11). */
export function isSendChord(ev: KeyboardEvent): boolean {
  return (ev.ctrlKey || ev.metaKey) && !ev.altKey && !ev.isComposing && ev.key === 'Enter';
}

/** Ctrl/Meta+Shift+C / +B reveal Cc / Bcc; returns which one or null. */
export function ccBccChord(ev: KeyboardEvent): 'cc' | 'bcc' | null {
  if (!(ev.ctrlKey || ev.metaKey) || ev.altKey || ev.isComposing || !ev.shiftKey) return null;
  if (ev.code === 'KeyC') return 'cc';
  if (ev.code === 'KeyB') return 'bcc';
  return null;
}
